/**
 * LlmCalls — Observability blade for LLM executions (the "executed
 * prompts" view). Phase 2 of the observability plan.
 *
 * Reads the same `operationalLogs` lx-obs pipeline the Op Logs viewer
 * uses, but narrowed to the LLM-call records the centralized executor
 * + lx-llm-caller emit:
 *
 *   - "All LLM calls"  → `lx_llm_caller.call_complete` (q filter):
 *       the UNIVERSAL per-call record — emitted for every LLM call
 *       (agent-status in-process, POST /api/llm/execute, harnesses).
 *       Carries provider/model/tokens/cost/duration/call_id + trace_id.
 *   - "Execute API"    → `llm-execute.served` (q filter):
 *       only HTTP /api/llm/execute calls — adds profile + org_id, but
 *       no cost_total (cost lives on the call_complete twin).
 *
 * Full prompt body + completion text are NOT in operationalLogs (only
 * lean metadata) — they live in Langfuse. Each row deep-links to its
 * Langfuse trace by `trace_id` when Langfuse export is enabled
 * (`getLangfuseLinkConfig`); otherwise the trace id renders as plain
 * mono text.
 *
 * Routed at `/observability/llm-calls`; sidebar "LLM Calls" under the
 * Observability group.
 */

import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowDown, ArrowUp, ExternalLink, Loader2, PanelRight, RefreshCw } from "lucide-react";
import { PageShell, PageHeader } from "@/components/layout/PageShell";
import { usePlatformApi } from "@/hooks/usePlatformApi";
import type { OperationalLogRecord } from "@/types/operational-log";

const PAGE_SIZE = 100;

const TIMEFRAME_OPTIONS = [
  { value: 1, label: "Last 1 hour" },
  { value: 6, label: "Last 6 hours" },
  { value: 24, label: "Last 24 hours" },
  { value: 72, label: "Last 3 days" },
  { value: 168, label: "Last 7 days" },
] as const;

// Each preset is a message marker matched server-side via the `q`
// (case-insensitive substring on `message`) filter — the only filter
// axis that distinguishes LLM records (subsystem is exact-match + varies
// per call site; provider/model live in `fields.*` which aren't
// server-filterable).
const PRESETS = [
  { value: "lx_llm_caller.call_complete", label: "All LLM calls" },
  { value: "llm-execute.served", label: "Execute API" },
] as const;

type PresetValue = (typeof PRESETS)[number]["value"];

interface LangfuseConfig {
  enabled: boolean;
  baseUrl: string;
  projectId: string;
}

interface TraceBodies {
  available: boolean;
  found: boolean;
  htmlUrl?: string;
  generations: Array<{ model?: string; input?: unknown; output?: unknown }>;
}

/** Render a Langfuse input/output value — pretty JSON for objects, raw
 *  text for strings, em-dash for empty. */
function renderBody(v: unknown): string {
  if (v == null) return "—";
  if (typeof v === "string") return v;
  try {
    return JSON.stringify(v, null, 2);
  } catch {
    return String(v);
  }
}

/** Uniform row shape normalised across the two record dialects. */
interface LlmCallRow {
  id: string;
  timestampMs: number;
  subsystem: string;
  traceId?: string;
  callId?: string;
  provider?: string;
  model?: string;
  profile?: string;
  orgId?: string;
  tokensIn?: number;
  tokensOut?: number;
  cost?: number;
  durationMs?: number;
  // Originator + source + prompt identity — populated once the
  // llm-caller lane ships the metadata-capture contract
  // (ai-docs/llm-call-metadata-capture-handoff.md). Absent until then;
  // the UI degrades to the subsystem + "—".
  promptSlug?: string;
  promptVersion?: number;
  originSource?: string;
  originUserId?: string;
  originUserSlug?: string;
  originOrgId?: string;
  originOrgSlug?: string;
  originHarnessName?: string;
  originCaseId?: string;
  raw: OperationalLogRecord;
}

function num(v: unknown): number | undefined {
  return typeof v === "number" && Number.isFinite(v) ? v : undefined;
}
function str(v: unknown): string | undefined {
  return typeof v === "string" && v !== "" ? v : undefined;
}

/**
 * call_complete uses input_tokens/output_tokens/duration_ms/cost_total;
 * llm-execute.served uses tokens_in/tokens_out/total_ms (no cost). The
 * `??` chain absorbs both dialects into one row. The `origin_*` /
 * `prompt_*` fields are the metadata-capture contract (snake_case keys
 * the llm-caller lane emits — see the handoff doc).
 */
function normalize(r: OperationalLogRecord): LlmCallRow {
  const f = r.fields ?? {};
  return {
    id: r._id ?? `${r.timestamp_ms}-${str(f.call_id) ?? str(f.correlation_id) ?? ""}`,
    timestampMs: r.timestamp_ms,
    subsystem: r.subsystem ?? "",
    traceId: r.trace_id,
    callId: str(f.call_id) ?? str(f.correlation_id),
    provider: str(f.provider),
    model: str(f.model),
    profile: str(f.profile),
    orgId: str(f.org_id),
    tokensIn: num(f.input_tokens) ?? num(f.tokens_in),
    tokensOut: num(f.output_tokens) ?? num(f.tokens_out),
    cost: num(f.cost_total),
    durationMs: num(f.duration_ms) ?? num(f.total_ms),
    promptSlug: str(f.prompt_slug),
    promptVersion: num(f.prompt_version),
    originSource: str(f.origin_source),
    originUserId: str(f.origin_user_id),
    originUserSlug: str(f.origin_user_slug),
    originOrgId: str(f.origin_org_id) ?? str(f.org_id),
    originOrgSlug: str(f.origin_org_slug),
    originHarnessName: str(f.origin_harness_name),
    originCaseId: str(f.origin_case_id),
    raw: r,
  };
}


// Cost is shown in MICRO-DOLLARS (µ$) — the "satoshi" of USD: 1 µ$ =
// $0.000001 (one millionth of a dollar). LLM call costs are tiny
// fractions ($0.00019), which read poorly; as integer µ$ they're clean
// (190 µ$). The exact dollar amount is in the `title` tooltip.
function microDollars(v: number | undefined): number | null {
  if (v == null) return null;
  return Math.round(v * 1_000_000);
}
function fmtCost(v: number | undefined): string {
  const u = microDollars(v);
  if (u == null) return "—";
  return `${u.toLocaleString()} µ$`;
}
function fmtCostTitle(v: number | undefined): string {
  if (v == null) return "";
  return `$${v.toFixed(6)} · 1 µ$ = $0.000001`;
}
function fmtMs(v: number | undefined): string {
  if (v == null) return "—";
  return v < 1000 ? `${v}ms` : `${(v / 1000).toFixed(1)}s`;
}
function fmtTime(ms: number): string {
  return new Date(ms).toLocaleString();
}
/** Date + time split, for the stacked (narrow) Time column. */
function fmtDateTimeParts(ms: number): { date: string; time: string } {
  const d = new Date(ms);
  return { date: d.toLocaleDateString(), time: d.toLocaleTimeString() };
}

/** Tokens in/out with directional arrows: ↑ input (prompt sent up to the
 *  model), ↓ output (completion generated back down). */
function TokenArrows({
  tokensIn,
  tokensOut,
}: {
  tokensIn?: number;
  tokensOut?: number;
}) {
  return (
    <span
      className="inline-flex items-center gap-2 font-mono"
      title="↑ input (prompt) · ↓ output (completion)"
    >
      <span className="inline-flex items-center gap-0.5">
        <ArrowUp className="h-3 w-3 text-sky-500" />
        {(tokensIn ?? 0).toLocaleString()}
      </span>
      <span className="inline-flex items-center gap-0.5">
        <ArrowDown className="h-3 w-3 text-emerald-500" />
        {(tokensOut ?? 0).toLocaleString()}
      </span>
    </span>
  );
}

// Build the dashboard deep-link whenever a URL is constructable
// (baseUrl + projectId present). Deliberately NOT gated on `enabled`
// (the export toggle): reading/linking works even if export is
// currently off — the trace just may not exist yet, which Langfuse
// surfaces on its side. The "export off" hint is shown separately.
function langfuseTraceUrl(
  cfg: LangfuseConfig | null,
  traceId: string | undefined,
): string | null {
  if (!cfg?.baseUrl || !cfg.projectId || !traceId) return null;
  const base = cfg.baseUrl.replace(/\/+$/, "");
  return `${base}/project/${cfg.projectId}/traces/${traceId}`;
}

export function LlmCalls() {
  const api = usePlatformApi();

  const [preset, setPreset] = useState<PresetValue>(
    "lx_llm_caller.call_complete",
  );
  const [hours, setHours] = useState<number>(24);
  // Client-side filters over the loaded page (the origin/* fields aren't
  // server-filterable yet — see the metadata-capture handoff).
  const [sourceFilter, setSourceFilter] = useState<string>("");
  const [searchText, setSearchText] = useState<string>("");
  const [rows, setRows] = useState<LlmCallRow[]>([]);
  const [cursor, setCursor] = useState<string | undefined>();
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [langfuse, setLangfuse] = useState<LangfuseConfig | null>(null);
  const [selected, setSelected] = useState<LlmCallRow | null>(null);
  // Full prompt + completion for the open row — lazy-fetched from the
  // Langfuse read proxy (the bodies aren't in operationalLogs).
  const [traceBodies, setTraceBodies] = useState<TraceBodies | null>(null);
  const [traceLoading, setTraceLoading] = useState(false);

  // Langfuse link config — best-effort; a failure just means rows show
  // the trace id as text instead of a link.
  useEffect(() => {
    let cancelled = false;
    void api
      .getLangfuseLinkConfig()
      .then((res) => {
        if (!cancelled) setLangfuse(res.data);
      })
      .catch(() => {
        if (!cancelled) setLangfuse(null);
      });
    return () => {
      cancelled = true;
    };
  }, [api]);

  const load = useCallback(
    async (append = false) => {
      if (append) setLoadingMore(true);
      else setLoading(true);
      try {
        const from = new Date(Date.now() - hours * 3600_000).toISOString();
        const res = await api.listOperationalLogs({
          q: preset,
          from,
          limit: PAGE_SIZE,
          cursor: append ? cursor : undefined,
        });
        const mapped = res.data.records.map(normalize);
        setRows((prev) => (append ? [...prev, ...mapped] : mapped));
        setCursor(res.data.nextCursor);
        setHasMore(Boolean(res.data.nextCursor));
        setError(null);
      } catch (e) {
        setError(e instanceof Error ? e.message : "Failed to load LLM calls");
      } finally {
        setLoading(false);
        setLoadingMore(false);
      }
    },
    [api, preset, hours, cursor],
  );

  // Reload from scratch whenever preset or timeframe changes.
  useEffect(() => {
    setCursor(undefined);
    void load(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preset, hours]);

  // Lazy-fetch the full prompt + completion from the Langfuse read proxy
  // when a row's detail drawer opens.
  useEffect(() => {
    if (!selected?.traceId) {
      setTraceBodies(null);
      return;
    }
    let cancelled = false;
    setTraceLoading(true);
    setTraceBodies(null);
    void api
      .getLlmCallTrace(selected.traceId)
      .then((res) => {
        if (!cancelled) setTraceBodies(res.data);
      })
      .catch(() => {
        if (!cancelled) setTraceBodies(null);
      })
      .finally(() => {
        if (!cancelled) setTraceLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [api, selected]);

  // Distinct sources present in the loaded page — drives the source
  // filter dropdown (empty until the metadata-capture contract lands).
  const sources = useMemo(() => {
    const set = new Set<string>();
    for (const r of rows) if (r.originSource) set.add(r.originSource);
    return [...set].sort();
  }, [rows]);

  // Client-side filtered view: source dropdown + free-text over the
  // originator / harness / case / provider / model fields.
  const displayedRows = useMemo(() => {
    const q = searchText.trim().toLowerCase();
    return rows.filter((r) => {
      if (sourceFilter && r.originSource !== sourceFilter) return false;
      if (!q) return true;
      const hay = [
        r.originUserSlug,
        r.originUserId,
        r.originOrgSlug,
        r.originOrgId,
        r.originHarnessName,
        r.originCaseId,
        r.promptSlug,
        r.provider,
        r.model,
        r.subsystem,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return hay.includes(q);
    });
  }, [rows, sourceFilter, searchText]);

  // Client-side summary over the FILTERED set (the aggregate endpoint
  // buckets by service+level, not LLM dimensions — so sum here).
  const summary = useMemo(() => {
    let tokensIn = 0;
    let tokensOut = 0;
    let cost = 0;
    let costKnown = false;
    for (const r of displayedRows) {
      tokensIn += r.tokensIn ?? 0;
      tokensOut += r.tokensOut ?? 0;
      if (r.cost != null) {
        cost += r.cost;
        costKnown = true;
      }
    }
    return { count: displayedRows.length, tokensIn, tokensOut, cost, costKnown };
  }, [displayedRows]);

  return (
    <PageShell data-testid="llm-calls-page">
      <PageHeader
        title="LLM Calls"
        subtitle="Executions over the centralized lx-llm-caller / execute pipeline · full prompt + completion bodies open in Langfuse"
        actions={
          <button
            type="button"
            onClick={() => void load(false)}
            disabled={loading}
            data-testid="llm-calls-refresh"
            className="inline-flex items-center gap-1.5 rounded-md border border-border bg-panel-2 px-3 py-1.5 text-sm font-medium text-muted transition-colors hover:bg-accent hover:text-foreground disabled:opacity-50"
          >
            <RefreshCw className={`h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`} />
            Refresh
          </button>
        }
      />

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3">
        <select
          value={preset}
          onChange={(e) => setPreset(e.target.value as PresetValue)}
          data-testid="llm-calls-preset"
          className="rounded-md border border-border bg-panel px-3 py-1.5 text-sm outline-none focus:border-primary"
        >
          {PRESETS.map((p) => (
            <option key={p.value} value={p.value}>
              {p.label}
            </option>
          ))}
        </select>
        <select
          value={hours}
          onChange={(e) => setHours(Number(e.target.value))}
          data-testid="llm-calls-timeframe"
          className="rounded-md border border-border bg-panel px-3 py-1.5 text-sm outline-none focus:border-primary"
        >
          {TIMEFRAME_OPTIONS.map((t) => (
            <option key={t.value} value={t.value}>
              {t.label}
            </option>
          ))}
        </select>
        {/* Source filter — populated from the loaded page's distinct
            origin_source values (empty until the metadata-capture
            contract lands). */}
        <select
          value={sourceFilter}
          onChange={(e) => setSourceFilter(e.target.value)}
          data-testid="llm-calls-source-filter"
          disabled={sources.length === 0}
          title={
            sources.length === 0
              ? "No source metadata yet — pending the llm-caller capture contract"
              : "Filter by originating source"
          }
          className="rounded-md border border-border bg-panel px-3 py-1.5 text-sm outline-none focus:border-primary disabled:opacity-50"
        >
          <option value="">All sources</option>
          {sources.map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </select>
        <input
          type="text"
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          placeholder="Search user / org / harness / case / model…"
          data-testid="llm-calls-search"
          className="min-w-[18rem] flex-1 rounded-md border border-border bg-panel px-3 py-1.5 text-sm outline-none placeholder:text-faint focus:border-primary"
        />
        {langfuse && !langfuse.enabled && (
          <span
            className="text-xs text-faint"
            data-testid="llm-calls-langfuse-off"
          >
            Langfuse export off — trace ids shown as text
          </span>
        )}
      </div>

      {/* Summary strip */}
      <div
        className="flex flex-wrap gap-6 rounded-lg border border-border bg-panel-2 px-4 py-3 text-sm"
        data-testid="llm-calls-summary"
      >
        <div>
          <span className="text-muted">Calls (loaded): </span>
          <span className="font-mono font-medium text-foreground">
            {summary.count}
          </span>
        </div>
        <div className="inline-flex items-center gap-1.5">
          <span className="text-muted">Tokens:</span>
          <TokenArrows
            tokensIn={summary.tokensIn}
            tokensOut={summary.tokensOut}
          />
        </div>
        <div>
          <span className="text-muted">Cost: </span>
          <span
            className="font-mono font-medium text-foreground"
            title={summary.costKnown ? fmtCostTitle(summary.cost) : ""}
          >
            {summary.costKnown ? fmtCost(summary.cost) : "n/a for this view"}
          </span>
        </div>
      </div>

      {error && (
        <div
          className="rounded-md border border-critical/40 bg-critical/10 p-3 text-sm text-critical"
          data-testid="llm-calls-error"
        >
          {error}
        </div>
      )}

      {/* Table */}
      {loading ? (
        <div className="flex items-center gap-2 py-8 text-sm text-muted">
          <Loader2 className="h-4 w-4 animate-spin" />
          Loading LLM calls…
        </div>
      ) : rows.length === 0 ? (
        <div
          className="rounded-lg border border-border bg-panel py-10 text-center text-sm text-muted"
          data-testid="llm-calls-empty"
        >
          No LLM calls in the selected window.
        </div>
      ) : displayedRows.length === 0 ? (
        <div
          className="rounded-lg border border-border bg-panel py-10 text-center text-sm text-muted"
          data-testid="llm-calls-filtered-empty"
        >
          No calls match the current source / search filter.
        </div>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-border">
          <table className="w-full text-sm" data-testid="llm-calls-table">
            <thead className="bg-panel-2 text-xs uppercase tracking-wider text-faint">
              <tr>
                <th className="whitespace-nowrap px-3 py-2 text-left font-medium">Time</th>
                {/* Origin absorbs the remaining width (w-full) + a floor so
                    the richer originator data has room when it lands. */}
                <th className="w-full min-w-[20rem] px-3 py-2 text-left font-medium">
                  Origin
                </th>
                <th className="whitespace-nowrap px-3 py-2 text-left font-medium">Model</th>
                <th
                  className="whitespace-nowrap px-3 py-2 text-right font-medium"
                  title="↑ input / ↓ output tokens"
                >
                  Tokens
                </th>
                <th className="whitespace-nowrap px-3 py-2 text-right font-medium">Cost (µ$)</th>
                <th className="whitespace-nowrap px-3 py-2 text-right font-medium">Dur</th>
                <th className="whitespace-nowrap px-3 py-2 text-center font-medium">Actions</th>
              </tr>
            </thead>
            <tbody>
              {displayedRows.map((r, i) => {
                const url = langfuseTraceUrl(langfuse, r.traceId);
                return (
                  <tr
                    key={r.id}
                    data-testid={`llm-calls-row-${i}`}
                    className="border-t border-border hover:bg-accent/40"
                  >
                    <td className="whitespace-nowrap px-3 py-2 text-xs leading-tight text-muted">
                      {(() => {
                        const { date, time } = fmtDateTimeParts(r.timestampMs);
                        return (
                          <>
                            <div>{date}</div>
                            <div className="font-mono text-faint">{time}</div>
                          </>
                        );
                      })()}
                    </td>
                    <td
                      className="w-full min-w-[20rem] px-3 py-2"
                      data-testid={`llm-calls-origin-${i}`}
                    >
                      {(() => {
                        const detail = [
                          r.originHarnessName,
                          r.originCaseId ? `case ${r.originCaseId}` : null,
                          r.originUserSlug ?? r.originUserId,
                          r.originOrgSlug ?? r.originOrgId,
                        ]
                          .filter(Boolean)
                          .join(" · ");
                        // Render the rich origin when ANY originator field is
                        // present (source badge optional) — so a defaulted
                        // org with no source still shows. Else fall back to
                        // the raw subsystem (the old "call site"). The prompt
                        // (slug+version) lives here too — it's part of the
                        // call's identity — linked to the Prompts page.
                        const hasOrigin = r.originSource || detail;
                        return (
                          <div className="flex flex-col gap-0.5">
                            {hasOrigin ? (
                              <span className="inline-flex flex-wrap items-center gap-1">
                                {r.originSource && (
                                  <span className="rounded bg-signal-soft px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-primary">
                                    {r.originSource}
                                  </span>
                                )}
                                {detail && (
                                  <span className="text-xs text-muted">{detail}</span>
                                )}
                              </span>
                            ) : (
                              <span
                                className="font-mono text-xs text-faint"
                                title="Originator metadata pending — showing the raw call-site subsystem"
                              >
                                {r.subsystem || "—"}
                              </span>
                            )}
                            {r.promptSlug && (
                              <Link
                                to={`/platform-prompts?slug=${encodeURIComponent(r.promptSlug)}`}
                                data-testid={`llm-calls-prompt-link-${i}`}
                                title="Open this prompt in Prompts"
                                className="inline-flex w-fit items-center gap-1 rounded bg-panel-2 px-1.5 py-0.5 font-mono text-[10px] text-muted hover:text-primary"
                              >
                                {r.promptSlug}
                                {r.promptVersion != null ? ` v${r.promptVersion}` : ""}
                              </Link>
                            )}
                          </div>
                        );
                      })()}
                    </td>
                    {/* Provider on top, model below — linked to LLM Providers. */}
                    <td className="whitespace-nowrap px-3 py-2 leading-tight">
                      <Link
                        to="/llm-providers"
                        data-testid={`llm-calls-model-link-${i}`}
                        title="Open LLM Providers"
                        className="block hover:text-primary"
                      >
                        <div className="text-foreground">{r.provider ?? "—"}</div>
                        <div className="font-mono text-xs text-muted">
                          {r.model ?? "—"}
                        </div>
                      </Link>
                      {r.profile && (
                        <div className="mt-0.5">
                          <span className="rounded bg-signal-soft px-1 py-0.5 text-[10px] font-medium text-primary">
                            {r.profile}
                          </span>
                        </div>
                      )}
                    </td>
                    <td className="whitespace-nowrap px-3 py-2 text-right">
                      <TokenArrows tokensIn={r.tokensIn} tokensOut={r.tokensOut} />
                    </td>
                    <td
                      className="whitespace-nowrap px-3 py-2 text-right font-mono"
                      title={fmtCostTitle(r.cost)}
                    >
                      {fmtCost(r.cost)}
                    </td>
                    <td className="whitespace-nowrap px-3 py-2 text-right font-mono">
                      {fmtMs(r.durationMs)}
                    </td>
                    {/* Actions — Details (open drawer) + Langfuse (open trace
                        in a new window). Replaces the row-click-to-detail. */}
                    <td className="px-3 py-2">
                      <div className="flex items-center justify-center gap-1">
                        <button
                          type="button"
                          onClick={() => setSelected(r)}
                          data-testid={`llm-calls-details-${i}`}
                          title="Details"
                          className="rounded-md p-1.5 text-muted transition-colors hover:bg-accent hover:text-foreground"
                        >
                          <PanelRight className="h-4 w-4" />
                        </button>
                        {url ? (
                          <a
                            href={url}
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid={`llm-calls-langfuse-${i}`}
                            title="Open the full prompt + completion trace in Langfuse (new window)"
                            className="rounded-md p-1.5 text-primary transition-colors hover:bg-signal-soft"
                          >
                            <ExternalLink className="h-4 w-4" />
                          </a>
                        ) : (
                          <span
                            data-testid={`llm-calls-trace-${i}`}
                            title={
                              r.traceId
                                ? `Langfuse trace ${r.traceId} — export off or trace not found`
                                : "No Langfuse trace"
                            }
                            className="cursor-not-allowed rounded-md p-1.5 text-faint"
                          >
                            <ExternalLink className="h-4 w-4" />
                          </span>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {hasMore && !loading && (
        <button
          type="button"
          onClick={() => void load(true)}
          disabled={loadingMore}
          data-testid="llm-calls-load-more"
          className="self-center rounded-md border border-border bg-panel-2 px-4 py-1.5 text-sm font-medium text-muted hover:bg-accent hover:text-foreground disabled:opacity-50"
        >
          {loadingMore ? "Loading…" : "Load more"}
        </button>
      )}

      {/* Detail drawer */}
      {selected && (
        <div
          className="fixed inset-0 z-50 flex justify-end bg-black/40"
          onClick={() => setSelected(null)}
          data-testid="llm-calls-detail-overlay"
        >
          <div
            className="h-full w-full max-w-lg overflow-y-auto border-l border-border bg-panel p-5 shadow-xl"
            onClick={(e) => e.stopPropagation()}
            data-testid="llm-calls-detail"
          >
            <div className="mb-4 flex items-center justify-between">
              <h2 className="text-lg font-semibold text-foreground">LLM call</h2>
              <button
                type="button"
                onClick={() => setSelected(null)}
                data-testid="llm-calls-detail-close"
                className="rounded-md px-2 py-1 text-sm text-muted hover:bg-accent hover:text-foreground"
              >
                Close
              </button>
            </div>
            <dl className="space-y-2 text-sm">
              <DetailRow label="Time" value={fmtTime(selected.timestampMs)} />
              {/* Origin — source + originator. Falls back to the raw
                  subsystem until the metadata-capture contract lands. */}
              <DetailRow
                label="Source"
                value={selected.originSource ?? "—"}
              />
              {selected.originHarnessName && (
                <DetailRow label="Harness" value={selected.originHarnessName} mono />
              )}
              {selected.originCaseId && (
                <DetailRow label="Case" value={selected.originCaseId} mono />
              )}
              {(selected.originUserSlug || selected.originUserId) && (
                <DetailRow
                  label="User"
                  value={selected.originUserSlug ?? selected.originUserId ?? "—"}
                  mono
                />
              )}
              <DetailRow
                label="Org"
                value={
                  selected.originOrgSlug ?? selected.originOrgId ?? selected.orgId ?? "—"
                }
                mono
              />
              {selected.promptSlug && (
                <DetailRow
                  label="Prompt"
                  value={`${selected.promptSlug}${selected.promptVersion != null ? ` v${selected.promptVersion}` : ""}`}
                  mono
                />
              )}
              <DetailRow label="Call site" value={selected.subsystem || "—"} mono />
              <DetailRow label="Provider" value={selected.provider ?? "—"} />
              <DetailRow label="Model" value={selected.model ?? "—"} mono />
              {selected.profile && (
                <DetailRow label="Profile" value={selected.profile} />
              )}
              <div className="flex justify-between gap-4 border-b border-border/50 pb-1.5">
                <dt className="shrink-0 text-muted">Tokens (↑in/↓out)</dt>
                <dd className="text-right">
                  <TokenArrows
                    tokensIn={selected.tokensIn}
                    tokensOut={selected.tokensOut}
                  />
                </dd>
              </div>
              <DetailRow
                label="Cost (µ$)"
                value={fmtCost(selected.cost)}
                title={fmtCostTitle(selected.cost)}
                mono
              />
              <DetailRow label="Duration" value={fmtMs(selected.durationMs)} mono />
              <DetailRow label="Call id" value={selected.callId ?? "—"} mono />
              <DetailRow label="Trace id" value={selected.traceId ?? "—"} mono />
            </dl>
            {/* [Langfuse] button — opens the full trace in a new window.
                Prefer the read-proxy's own htmlUrl (authoritative), fall
                back to the config-built URL. */}
            {(() => {
              const url =
                traceBodies?.htmlUrl ??
                langfuseTraceUrl(langfuse, selected.traceId);
              return url ? (
                <a
                  href={url}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="llm-calls-detail-langfuse"
                  className="mt-4 inline-flex items-center gap-1.5 rounded-md bg-primary px-3 py-2 text-sm font-medium text-white hover:bg-primary/90"
                >
                  <ExternalLink className="h-4 w-4" /> Open in Langfuse
                </a>
              ) : null;
            })()}

            {/* Full prompt + completion, fetched from the Langfuse read
                proxy (the bodies aren't in operationalLogs). */}
            <div className="mt-4" data-testid="llm-calls-detail-bodies">
              <h3 className="mb-2 text-sm font-semibold text-foreground">
                Prompt + completion
              </h3>
              {traceLoading ? (
                <div className="flex items-center gap-2 text-sm text-muted">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Loading from Langfuse…
                </div>
              ) : traceBodies?.available && traceBodies.found ? (
                <div className="space-y-3" data-testid="llm-calls-detail-bodies-found">
                  {traceBodies.generations.map((g, gi) => (
                    <div key={gi} className="space-y-2">
                      {g.model && (
                        <div className="font-mono text-xs text-muted">{g.model}</div>
                      )}
                      <div>
                        <div className="mb-1 text-xs font-medium text-muted">Prompt (input)</div>
                        <pre className="max-h-72 overflow-auto whitespace-pre-wrap break-words rounded bg-panel-2 p-2 text-xs text-foreground">
                          {renderBody(g.input)}
                        </pre>
                      </div>
                      <div>
                        <div className="mb-1 text-xs font-medium text-muted">Completion (output)</div>
                        <pre className="max-h-72 overflow-auto whitespace-pre-wrap break-words rounded bg-panel-2 p-2 text-xs text-foreground">
                          {renderBody(g.output)}
                        </pre>
                      </div>
                    </div>
                  ))}
                </div>
              ) : traceBodies?.available && !traceBodies.found ? (
                <p
                  className="text-xs text-faint"
                  data-testid="llm-calls-detail-not-found"
                >
                  This call's trace isn't in Langfuse — export was likely off
                  (LANGFUSE_ENABLED) when it ran. Enable export to capture the
                  full prompt + completion for future calls.
                </p>
              ) : (
                <p
                  className="text-xs text-faint"
                  data-testid="llm-calls-detail-no-langfuse"
                >
                  Langfuse read credentials aren't configured, so the full
                  bodies can't be fetched. Set LANGFUSE_PUBLIC_KEY /
                  LANGFUSE_SECRET_KEY / LANGFUSE_BASE_URL on lexserver.
                </p>
              )}
            </div>
          </div>
        </div>
      )}
    </PageShell>
  );
}

function DetailRow({
  label,
  value,
  mono,
  title,
}: {
  label: string;
  value: string;
  mono?: boolean;
  title?: string;
}) {
  return (
    <div className="flex justify-between gap-4 border-b border-border/50 pb-1.5">
      <dt className="shrink-0 text-muted">{label}</dt>
      <dd
        title={title}
        className={`text-right text-foreground ${mono ? "break-all font-mono text-xs" : ""}`}
      >
        {value}
      </dd>
    </div>
  );
}
